import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { adminControlleDashboardURl } from "../../assets/api/BaseURL";
import { getDisplayCurrency } from "../../../utils/currencyUtils";

const OrderCountDashboard = ({ token }) => {
    const [isLoading, setIsLoading] = useState(false);

    // ✅ Default updated
    const [range, setRange] = useState("last_30_days");

    const [dashboardData, setDashboardData] = useState({});

    const safeNumber = (value) => {
        const num = Number(value);
        return isNaN(num) ? 0 : num;
    };

    const formatCurrency = (value) => {
        return safeNumber(value).toFixed(2);
    };

    useEffect(() => {
        if (!token) return;

        const getData = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(
                    `${adminControlleDashboardURl}?range=${range}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (response.status === 200 && response.data?.data) {
                    setDashboardData(response.data.data);
                } else {
                    setDashboardData({});
                    toast.info("No data found");
                }
            } catch (error) {
                setDashboardData({});
                toast.error(error?.response?.data?.message || error.message);
            } finally {
                setIsLoading(false);
            }
        };

        getData();
    }, [token, range]);

    const displayCurrency = getDisplayCurrency(
        localStorage.getItem("currencySymbol"),
        localStorage.getItem("currencyCode")
    );

    /* ❌ No Data Check */
    const isNoData =
        !dashboardData ||
        Object.values(dashboardData).every((val) => safeNumber(val) === 0);

    return (
        <div className="row">
            <div className="col-12">

                {/* 🔷 Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h3 className="fw-bold mb-0">Order Overview</h3>

                    {/* ✅ Updated Filters */}
                    <select
                        className="form-select form-select-sm"
                        style={{ width: "180px" }}
                        value={range}
                        onChange={(e) => setRange(e.target.value)}
                        disabled={isLoading}
                    >
                        <option value="today">Today</option>
                        <option value="yesterday">Yesterday</option>
                        <option value="last_7_days">Last 7 Days</option>
                        <option value="last_30_days">Last 30 Days</option>
                        <option value="this_month">This Month</option>
                        <option value="last_month">Last Month</option>
                        <option value="last_90_days">Last 90 Days</option>
                    </select>
                </div>

                {/* 📊 Content */}
                {isLoading ? (
                    <p className="text-center">Loading...</p>
                ) : isNoData ? (
                    <div className="text-center">
                        <p className="mb-1 text-muted">No Data Available</p>
                        <small className="text-muted">
                            No orders found for selected range
                        </small>
                    </div>
                ) : (
                    <div className="row g-3">

                        <div className="col-xl-2 col-lg-4 col-md-6">
                            <div className="p-3 rounded-4 shadow-sm bg-white h-100">
                                <p className="text-muted small mb-1">Total Orders</p>
                                <h3 className="fw-bold mb-0">
                                    {safeNumber(dashboardData.total_orders)}
                                </h3>
                            </div>
                        </div>

                        <div className="col-xl-2 col-lg-4 col-md-6">
                            <div className="p-3 rounded-4 shadow-sm bg-white h-100">
                                <p className="text-muted small mb-1">Total Sales</p>
                                <h3 className="fw-bold mb-0">
                                    {displayCurrency}{formatCurrency(dashboardData.total_amount)}
                                </h3>
                            </div>
                        </div>

                        <div className="col-xl-2 col-lg-4 col-md-6">
                            <div className="p-3 rounded-4 shadow-sm bg-white h-100">
                                <p className="text-muted small mb-1">Paid Amount</p>
                                <h3 className="fw-bold mb-0">
                                    {displayCurrency}{formatCurrency(dashboardData.settled_amount)}
                                </h3>
                                <span className="text-success small">Paid</span>
                            </div>
                        </div>

                        <div className="col-xl-2 col-lg-4 col-md-6">
                            <div className="p-3 rounded-4 shadow-sm bg-white h-100">
                                <p className="text-muted small mb-1">Pending Amount</p>
                                <h3 className="fw-bold mb-0">
                                    {displayCurrency}{formatCurrency(dashboardData.unsettled_amount)}
                                </h3>
                                <span className="text-warning small">Pending</span>
                            </div>
                        </div>

                        <div className="col-xl-2 col-lg-4 col-md-6">
                            <div className="p-3 rounded-4 shadow-sm bg-white h-100">
                                <p className="text-danger small mb-1">Cancelled Orders</p>
                                <h3 className="fw-bold mb-0">
                                    {safeNumber(dashboardData.cancelled_orders)}
                                </h3>
                                <span className="text-muted small">
                                    Amount : {displayCurrency}{formatCurrency(dashboardData.cancelled_amount)}
                                </span>
                            </div>
                        </div>

                        <div className="col-xl-2 col-lg-4 col-md-6">
                            <div className="p-3 rounded-4 shadow-sm bg-white h-100">
                                <p className="text-warning small mb-1">Orders on Hold </p>
                                <h3 className="fw-bold mb-0">
                                    {safeNumber(dashboardData.hold_orders)}
                                </h3>
                                <span className="text-muted small">
                                    Amount : {displayCurrency}{formatCurrency(dashboardData.hold_amount)}
                                </span>
                            </div>
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
};

export default OrderCountDashboard;